import ApiService from './api.service';

export interface DevelopGitInfo {
  /** "owner/name" of the Pliable-owned repo, or null if none provisioned. */
  repo: string | null;
  branch?: string;
  /** GitHub URL to the develop branch. */
  url?: string;
  /** Current head SHA of the develop branch, or null. */
  sha?: string | null;
  /** SHA of the develop commit last imported into Develop, or null. */
  synced_sha?: string | null;
  /** True when the current Pliable branch reflects its git branch head. */
  in_sync?: boolean;
  /** Which Pliable branch this describes — DRAFT or PROD. */
  pliable_branch?: string;
  /** False when the repo maps no branch for the current Pliable branch (e.g. in
   *  Production with no production branch set). */
  mapped?: boolean;
}

export interface DevelopCommit {
  sha: string;
  short_sha: string;
  /** Full commit message (markdown, as authored by `plb sync`). */
  message: string;
  author?: string | null;
  date?: string | null;
  /** GitHub URL to the commit. */
  url?: string | null;
}

export interface DevelopCommitsPage {
  commits: DevelopCommit[];
  has_more: boolean;
}

export interface PushJobStatus {
  ok?: boolean;
  /** RQ job status: queued | started | deferred | finished | failed. */
  job_status: string;
  job_result?: { pushed?: boolean; sha?: string } | null;
  error_message?: string | null;
  progress?: unknown;
}

/** How a builder's local changes reach the branch Pliable imports from. */
export type GitSyncWorkflow = 'DIRECT' | 'PULL_REQUEST';

/** Which GitHub App authenticates to the repo. */
export type GitRepoAuthMode = 'PLIABLE_APP' | 'CUSTOMER_APP';

export interface SourceRepoConfig {
  /** False when the tenant has no repo at all. */
  configured: boolean;
  repo?: string;
  auth_mode?: GitRepoAuthMode;
  /** The branch Pliable imports from. */
  branch?: string;
  /** Path to the dbt project inside the repo; null when it is at the root. */
  project_subdir?: string | null;
  /** The branch backing Pliable's Production; null when publishing stays in-platform. */
  prod_branch?: string | null;
  /** True when publishing merges in git rather than copying inside Pliable. */
  publishes_through_git?: boolean;
  sync_workflow?: GitSyncWorkflow;
  /** When true, the platform never writes to git — the repo is the only way in. */
  writes_disabled?: boolean;
  /** Run `dbt deps` before each build (needed for a project with packages.yml). */
  dbt_deps_enabled?: boolean;
  /** Whether the customer-facing GitHub App has been linked to this tenant. */
  github_installed?: boolean;
  /** Seconds a Develop change waits before it is pushed to the branch. */
  develop_sync_delay_seconds?: number;
  /**
   * True once something has been imported. The repo and project directory are
   * locked after that: changing them would delete every model the new project
   * does not contain.
   */
  synced?: boolean;
}

export interface SourceRepoUpdate {
  full_name: string;
  auth_mode?: GitRepoAuthMode;
  branch?: string;
  prod_branch?: string | null;
  project_subdir?: string | null;
  sync_workflow?: GitSyncWorkflow;
  writes_disabled?: boolean;
  dbt_deps_enabled?: boolean;
}

export interface GithubRepoOption {
  id: string;
  full_name: string;
  name: string;
  /** The repo's own default branch, straight from GitHub. Used to pre-fill the
   *  import branch so nobody has to notice that our field defaults to `develop`. */
  default_branch?: string;
}

class GitService {
  async getDevelop(): Promise<DevelopGitInfo> {
    return (await ApiService.getInstance().request('GET', '/git/develop')) as DevelopGitInfo;
  }

  async pushDevelop(): Promise<{ job_id: string }> {
    return (await ApiService.getInstance().request('POST', '/git/push-develop')) as {
      job_id: string;
    };
  }

  async pullProd(): Promise<{ job_id: string }> {
    return (await ApiService.getInstance().request('POST', '/git/pull-prod')) as {
      job_id: string;
    };
  }

  async pullDevelop(): Promise<{ job_id: string }> {
    return (await ApiService.getInstance().request('POST', '/git/pull-develop')) as {
      job_id: string;
    };
  }

  async getSourceRepo(): Promise<SourceRepoConfig> {
    return (await ApiService.getInstance().request('GET', '/source-repo')) as SourceRepoConfig;
  }

  async setSourceRepo(body: SourceRepoUpdate): Promise<SourceRepoConfig> {
    return (await ApiService.getInstance().request('PUT', '/source-repo', body)) as SourceRepoConfig;
  }

  async disconnectSourceRepo(): Promise<{ status: string }> {
    return (await ApiService.getInstance().request('DELETE', '/source-repo')) as {
      status: string;
    };
  }

  async importSourceRepo(): Promise<{ job_id: string }> {
    return (await ApiService.getInstance().request('POST', '/source-repo/import')) as {
      job_id: string;
    };
  }

  /** Repos the customer granted our GitHub App access to. */
  async getAvailableRepos(): Promise<GithubRepoOption[]> {
    const response = (await ApiService.getInstance().request('GET', '/github/repos')) as {
      records: GithubRepoOption[];
    };
    return response.records ?? [];
  }

  async getDevelopCommits(page = 1, perPage = 20): Promise<DevelopCommitsPage> {
    return (await ApiService.getInstance().request(
      'GET',
      `/git/develop/commits?page=${page}&per_page=${perPage}`
    )) as DevelopCommitsPage;
  }

  async getJobStatus(jobId: string): Promise<PushJobStatus> {
    return (await ApiService.getInstance().request(
      'GET',
      `/get-job-status/${jobId}`
    )) as PushJobStatus;
  }

  /** Recent import attempts, newest first. */
  async listImports(): Promise<{ imports: ImportRun[] }> {
    return (await ApiService.getInstance().request(
      'GET', '/git/imports'
    )) as { imports: ImportRun[] };
  }

  /** The one unacknowledged import failure the app should be shouting about. */
  async getImportAlert(): Promise<{ alert: ImportRun | null }> {
    return (await ApiService.getInstance().request(
      'GET', '/git/imports/alert'
    )) as { alert: ImportRun | null };
  }

  /** Dismiss one failure. Per-failure, so the next one shows again. */
  async acknowledgeImport(id: string): Promise<{ status: string }> {
    return (await ApiService.getInstance().request(
      'POST', `/git/imports/${id}/acknowledge`
    )) as { status: string };
  }

  /** Re-run a failed import — same branch, same environment. */
  async retryImport(id: string): Promise<{ job_id: string }> {
    return (await ApiService.getInstance().request(
      'POST', `/git/imports/${id}/retry`
    )) as { job_id: string };
  }
}

export const gitService = new GitService();

/** RQ job statuses that mean the push is done (success or failure). */
export const TERMINAL_JOB_STATUSES = new Set(['finished', 'failed', 'stopped', 'canceled']);

/** One attempt to import a git project into Pliable. */
export interface ImportRun {
  id: string;
  trigger: 'WEBHOOK' | 'MANUAL' | 'PUBLISH' | 'UPLOAD';
  status: 'RUNNING' | 'SUCCESS' | 'FAILED' | 'SKIPPED';
  repo: string | null;
  git_branch: string | null;
  commit_sha: string | null;
  /** The Pliable environment it wrote to. */
  target_branch: string;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  error_type: string | null;
  /** Why a SKIPPED run did nothing — e.g. a push to an unmapped branch. */
  skipped_reason: string | null;
  /** Problems that did not stop the import, such as an analysis that could not
   *  be materialized. The import succeeded, but incompletely. */
  warnings: string[];
  stats: Record<string, unknown>;
  triggered_by: string | null;
  summary: string;
  can_retry: boolean;
}
